import { useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { MessageSquare, AlertCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLE_QUESTIONS = [
  "Can the committee increase levies without a vote?",
  "What's the quorum for an AGM?",
  "Do I need permission to install an air conditioner on the balcony?",
  "Who pays when a common-property pipe leaks into my unit?",
];

import { streamSSE } from "@/lib/sseStream";

export default function OwnerQA() {
  const [state, setState] = useState("NSW");
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [liveAnswer, setLiveAnswer] = useState("");
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const ask = async (q) => {
    const finalQ = (q || question).trim();
    if (finalQ.length < 3) {
      toast.error("Please type a question first.");
      return;
    }
    setQuestion(finalQ);
    setLoading(true); setStreaming(true);
    setError(null); setResult(null); setLiveAnswer("");
    await streamSSE({
      url: `${API}/ai/owner-qa`,
      body: { scheme_state: state, question: finalQ },
      onEvent: ({ event, data }) => {
        if (event === "token") {
          setLoading(false);
          setLiveAnswer((p) => p + (data?.text || ""));
        } else if (event === "done") {
          setResult(data); setStreaming(false); setLoading(false);
        } else if (event === "error") {
          setError(data?.message || "AI provider error");
          toast.error("Could not get an answer.");
          setStreaming(false); setLoading(false);
        }
      },
      onError: (err) => {
        setError(err?.message || "Failed to get an answer.");
        toast.error("Could not get an answer.");
        setStreaming(false); setLoading(false);
      },
    });
  };

  return (
    <Card
      className="border rounded-xl shadow-sm bg-white"
      style={{ borderColor: "var(--border)" }}
      data-testid="owner-qa-card"
    >
      <CardHeader className="pb-4">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{
              width: 32,
              height: 32,
              background: "var(--blue-light)",
              color: "var(--blue-dark)",
            }}
          >
            <MessageSquare size={16} strokeWidth={2.25} />
          </div>
          <div>
            <CardTitle
              className="text-base font-bold"
              style={{ color: "var(--navy)" }}
            >
              Owner Q&amp;A
            </CardTitle>
            <CardDescription
              className="text-xs"
              style={{ color: "var(--slate)" }}
            >
              Plain-English answers grounded in state strata legislation.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label
            htmlFor="state-qa"
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: "var(--slate)", letterSpacing: "0.08em" }}
          >
            Scheme state
          </Label>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger
              id="state-qa"
              className="h-10"
              data-testid="qa-state-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NSW" data-testid="qa-state-NSW">
                NSW — Strata Schemes Management Act 2015
              </SelectItem>
              <SelectItem value="QLD" data-testid="qa-state-QLD">
                QLD — BCCM Act 1997
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label
            htmlFor="question"
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: "var(--slate)", letterSpacing: "0.08em" }}
          >
            Question
          </Label>
          <Textarea
            id="question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. Can the committee increase levies without a vote?"
            className="min-h-[100px] resize-y"
            style={{ fontSize: "0.9rem" }}
            data-testid="qa-question-input"
          />
          <div className="flex flex-wrap gap-1.5 pt-1">
            {SAMPLE_QUESTIONS.map((q, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setQuestion(q)}
                className="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                style={{
                  borderColor: "var(--border)",
                  color: "var(--slate)",
                  background: "var(--slate-light)",
                }}
                data-testid={`qa-sample-${i}`}
              >
                {q}
              </button>
            ))}
          </div>
        </div>

        <Button
          onClick={() => ask()}
          disabled={loading}
          className="w-full h-11 font-semibold"
          style={{ background: "var(--blue)", color: "white" }}
          data-testid="qa-ask-btn"
        >
          {loading ? (
            <span className="inline-flex items-center gap-2">
              Thinking
              <span className="inline-flex gap-1 ml-1">
                <span className="ai-dot" />
                <span className="ai-dot" />
                <span className="ai-dot" />
              </span>
            </span>
          ) : (
            "Ask"
          )}
        </Button>

        {error && (
          <div
            className="flex items-start gap-2 rounded-lg p-3 text-sm"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="qa-error"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {streaming && liveAnswer && !result && (
          <div
            className="rounded-lg border bg-white p-4"
            style={{ borderColor: "var(--blue)" }}
            data-testid="qa-live-answer"
          >
            <div className="flex items-center gap-2 mb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: "var(--blue)" }}>
              <span className="ai-dot" data-testid="qa-streaming-indicator"/> Streaming…
            </div>
            <pre className="whitespace-pre-wrap text-sm font-sans" style={{ color: "var(--navy)" }} data-testid="qa-live-text">{liveAnswer}</pre>
          </div>
        )}

        {result && (
          <div
            className="rounded-lg border bg-white"
            style={{ borderColor: "var(--border)" }}
            data-testid="qa-result"
          >
            <div
              className="flex items-center justify-between px-4 py-2.5 border-b"
              style={{ borderColor: "var(--border)" }}
            >
              <span
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider"
                style={{
                  color: "var(--blue-dark)",
                  letterSpacing: "0.12em",
                }}
              >
                ● AI answer — {state}
              </span>
            </div>
            <div className="px-5 py-4 markdown-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {result.answer}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </CardContent>

      {result && (
        <CardFooter
          className="block border-t pt-3"
          style={{ borderColor: "var(--border)" }}
        >
          <p
            className="text-[11px] italic leading-snug"
            style={{ color: "var(--tagline-grey)" }}
            data-testid="qa-disclaimer"
          >
            {result.disclaimer}
          </p>
        </CardFooter>
      )}
    </Card>
  );
}
